import React, { createContext, FC, useContext, useEffect, useRef, useState } from 'react'

import { useMutation } from '@apollo/client'

import { authState, signIn } from 'fireChat'
import useChatCustomer from 'Hooks/useChatCustomer'
import useEvent from 'Hooks/useEvent'
import { ChatCustomer } from 'Types'
import { signUpCallback } from 'Utils/signUpCallback'

import { SIGN_IN_ANONYMOUSLY } from '../const/gql/signInAnonymously'

interface Context {
  loading: boolean
  signInAnonymously: () => Promise<unknown>
  uid: string | undefined
}

const AuthContext = createContext<Context>({
  uid: undefined,
  loading: false,
  signInAnonymously: () => Promise.resolve(),
})

interface Props {
  children: React.ReactNode
}

const AuthProvider: FC<Props> = ({ children }) => {
  const [uid, setUid] = useState<string>()
  const signingRef = useRef(false)

  const [customer, { loading }] = useChatCustomer()
  const [signInAnonymously] = useMutation(SIGN_IN_ANONYMOUSLY, {
    update(cache, { data }) {
      cache.modify({
        fields: {
          currentCustomer() {
            return {
              ...(data.signInAnonymouslyCustomer.customer as ChatCustomer),
              __typename: 'Customer',
            }
          },
        },
      })
    },
  })

  const firebaseToken = customer?.firebaseToken

  useEffect(() => {
    if (!firebaseToken) return

    let cancelled = false
    void signIn(firebaseToken).then(() => {
      if (cancelled) return
      setUid(authState.uid)
    })

    return () => {
      cancelled = true
    }
  }, [firebaseToken])

  const handleSignInAnonymously = useEvent(() => {
    if (uid || signingRef.current || authState.signing) return Promise.reject(new Error())

    signingRef.current = true
    return signInAnonymously()
      .then(() => signUpCallback())
      .finally(() => {
        signingRef.current = true
      })
  })

  return (
    <AuthContext.Provider value={{ uid, signInAnonymously: handleSignInAnonymously, loading }}>
      {children}
    </AuthContext.Provider>
  )
}

const useChatAuth = (): Context => useContext(AuthContext)

export default AuthProvider
export { useChatAuth }
